/**
 * Webhook signature verification utilities.
 *
 * Supports both Web Crypto API (browsers, Deno, Cloudflare Workers)
 * and Node.js crypto module with automatic runtime detection.
 * Uses constant-time comparison to prevent timing attacks.
 */

/**
 * Verify an HMAC-SHA256 webhook signature.
 *
 * @param payload - The raw request body string
 * @param signature - The signature from the webhook header
 * @param secret - The webhook signing secret
 * @returns true if the signature is valid
 */
export async function verifyWebhookSignature(
  payload: string,
  signature: string,
  secret: string
): Promise<boolean> {
  const hexDigest = await computeHmacSha256(payload, secret);

  // Backend sends "sha256=<hex>" format — support both prefixed and raw signatures
  const expectedPrefixed = `sha256=${hexDigest}`;

  if (signature.startsWith('sha256=')) {
    return constantTimeEqual(signature, expectedPrefixed);
  }

  return constantTimeEqual(signature, hexDigest);
}

/**
 * Compute HMAC-SHA256 hex digest.
 * Automatically selects Web Crypto API or Node.js crypto.
 */
async function computeHmacSha256(message: string, secret: string): Promise<string> {
  // Try Web Crypto API first (browsers, Deno, Cloudflare Workers, Node 18+)
  if (typeof globalThis.crypto !== 'undefined' && globalThis.crypto.subtle) {
    const encoder = new TextEncoder();
    const key = await globalThis.crypto.subtle.importKey(
      'raw',
      encoder.encode(secret),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    const sig = await globalThis.crypto.subtle.sign('HMAC', key, encoder.encode(message));
    return Array.from(new Uint8Array(sig))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // Fallback to Node.js crypto module
  try {
    const { createHmac } = await import('crypto');
    return createHmac('sha256', secret).update(message).digest('hex');
  } catch {
    throw new Error(
      'No crypto implementation available. Requires Web Crypto API or Node.js crypto module.'
    );
  }
}

/**
 * Constant-time string comparison to prevent timing attacks.
 */
function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) {
    return false;
  }

  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
}
